import { useEffect, useState } from "react";

import FeatureGates from "@atlassian/feature-gate-js-client";
import { useActivityClient, ActivityItem } from "@atlassian/recent-work-client";
import { MeResponse } from "@atlassiansox/asc-stargate";

import {
    AisSitesType,
    AvailableProductsResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src/index";

import { useLoggedInUser } from "../../../../../src/utils/logged-in-user-provider";

const GRAPHQL_GATEWAY_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com/gateway/api/graphql"
        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

export enum AtlSiteType {
    CONFLUENCE = "confluence.ondemand",
    JIRA = "jira-software.ondemand"
}

export interface useLogMeInProps {
    availableProducts: AvailableProductsResponse | null;
    userProfile: MeResponse | null;
    siteType: AtlSiteType;
}

export interface useLogMeInResponse {
    userProfileName: string | undefined;
    recentSites: AisSitesType[];
    shouldShowLogMeIn: boolean;
    loading: boolean;
}

export const filterAvailableSites = (
    availableProducts: AvailableProductsResponse | null,
    siteType: AtlSiteType
): AisSitesType[] => {
    if (availableProducts) {
        const filteredSites =
            availableProducts.sites.filter((site) => {
                return site.products.includes(siteType);
            }) || [];

        return filteredSites;
    } else {
        return [];
    }
};

export const constructLoginUrl = (url: string, siteType: AtlSiteType) => {
    const loginUrl = new URL(url);
    switch (siteType) {
        case AtlSiteType.JIRA:
            loginUrl.pathname = "/login.jsp";
            const searchParam = new URLSearchParams();
            const browseUrl = new URL(loginUrl);
            browseUrl.pathname = "/browse";
            searchParam.append("os_destination", browseUrl.href);
            loginUrl.search = searchParam.toString();
            return loginUrl;
        case AtlSiteType.CONFLUENCE:
            loginUrl.pathname = "/wiki";
            loginUrl.search = "";
            return loginUrl;
        default:
            return loginUrl;
    }
};

export const useLogMeIn = ({
    availableProducts,
    userProfile,
    siteType = AtlSiteType.CONFLUENCE
}: useLogMeInProps): useLogMeInResponse => {
    const [filteredSites, setFilteredSites] = useState(
        filterAvailableSites(availableProducts, siteType)
    );

    // Loading if sites call failed or there is more than 1 site
    const [loading, setLoading] = useState(
        !availableProducts ||
            availableProducts.callFailed ||
            availableProducts.sites.length > 1
    );

    const loggedInUserName = useLoggedInUser().name;
    const userProfileName = userProfile?.name || loggedInUserName;

    const stargateClient = useBxpWebStargateClient();
    const activityClient = useActivityClient(
        "v3",
        "www.atlassian.com",
        undefined,
        GRAPHQL_GATEWAY_URL
    );

    useEffect(() => {
        let filteredSitesPromise: Promise<AisSitesType[]> = Promise.resolve(
            filterAvailableSites(availableProducts, siteType)
        );
        let activitiesPromise: Promise<ActivityItem | null | undefined> =
            Promise.resolve(null);

        // only make these calls if we have a userProfile, using name as a hint
        if (userProfileName) {
            // Fetch available-products if not available from SSR
            // only call it once though
            if (!availableProducts || availableProducts.callFailed) {
                filteredSitesPromise = stargateClient
                    .availableProducts({})
                    .then((data) => filterAvailableSites(data, siteType))
                    .catch((error) => {
                        console.log(error);
                        return [];
                    });
            }

            // Fetch recent activity only if we know the user is logged in and has a name
            activitiesPromise = activityClient
                .fetchActivities(
                    [50, 125, 250], // retries
                    {
                        limit: 5,
                        products:
                            siteType === AtlSiteType.CONFLUENCE
                                ? ["confluence"]
                                : ["jira"]
                    },
                    ["workedOn", "viewed"]
                )
                .then((data) => {
                    // Which is more recent? The newest viewed or the newest worked on object?
                    const mostRecentlyViewed = data.viewed[0];
                    const mostRecentlyWorkedOn = data.workedOn[0];

                    if (mostRecentlyViewed && mostRecentlyWorkedOn) {
                        return new Date(mostRecentlyViewed.timestamp) >
                            new Date(mostRecentlyWorkedOn.timestamp)
                            ? mostRecentlyViewed
                            : mostRecentlyWorkedOn;
                    } else if (mostRecentlyWorkedOn) {
                        return mostRecentlyWorkedOn;
                    }

                    return mostRecentlyViewed ?? null;
                })
                .catch((error) => {
                    console.log(error);
                    return null;
                });
        }

        // Set state after receiving all responses
        Promise.all([filteredSitesPromise, activitiesPromise]).then(
            ([filteredSitesData, mostRecentActivity]) => {
                if (filteredSitesData.length > 1 && mostRecentActivity) {
                    const loginUrl = constructLoginUrl(
                        mostRecentActivity.object.url,
                        siteType
                    );

                    const recentSiteIndex = filteredSitesData.findIndex(
                        (site) => loginUrl.host === new URL(site.url).host
                    );

                    // shift the most recent site to the front of the array
                    if (recentSiteIndex > 0) {
                        filteredSitesData.unshift(
                            ...filteredSitesData.splice(recentSiteIndex, 1)
                        );
                        setFilteredSites(filteredSitesData);
                    } else {
                        setFilteredSites(filteredSitesData);
                    }
                } else {
                    setFilteredSites(filteredSitesData);
                }
                setLoading(false);
            }
        );
    }, [
        stargateClient,
        activityClient,
        availableProducts,
        siteType,
        userProfileName
    ]);

    let shouldShowLogMeIn = !!userProfileName && filteredSites.length > 0;

    if (shouldShowLogMeIn && siteType === AtlSiteType.CONFLUENCE) {
        // only expose the user if they are logged in and have recentSites
        const logMeInEnrollment = FeatureGates.getExperimentValue(
            "cfind-1109-confluence-log-me-back-in",
            "enrolled",
            false
        );
        shouldShowLogMeIn = shouldShowLogMeIn && logMeInEnrollment;
    }

    return {
        userProfileName,
        recentSites: filteredSites,
        shouldShowLogMeIn,
        loading
    };
};
